import { History } from "lucide-react";
import { Link } from "react-router";

import type { PatientResponseDto } from "~/lib/api";

interface CarriedItem {
  patientId: string;
  patientName: string;
  bedNumber: string;
  description: string;
}

/**
 * Flatten patients → their carried-over, still-open tasks. A task is carried
 * over (HAN-82) when `carriedFromTaskId` is set — copied from the previous
 * shift's session and overdue from the start.
 */
function collectCarried(patients: PatientResponseDto[]): CarriedItem[] {
  const items: CarriedItem[] = [];
  for (const p of patients) {
    // An acknowledged patient is signed-off; its tasks are no longer actionable,
    // so they drop out of the banner — same posture as overdue/escalated.
    if (p.acknowledgedAt != null) continue;
    for (const t of p.tasks) {
      if (t.completedAt == null && t.carriedFromTaskId != null) {
        items.push({
          patientId: p.id,
          patientName: p.fullName,
          bedNumber: p.bedNumber,
          description: t.description,
        });
      }
    }
  }
  return items;
}

/**
 * HAN-82 (V2-E8.2) top-of-screen banner listing the still-open tasks carried
 * over from the previous handover, each linking to its patient. Renders nothing
 * when there are none. Indigo + role="status" — informative and sticky, but
 * deliberately distinct from the red (overdue) and amber (escalated) banners so
 * the three states read as different.
 */
export function CarriedOverTasksBanner({
  sessionId,
  patients,
}: {
  sessionId: string;
  patients: PatientResponseDto[];
}): JSX.Element | null {
  const carried = collectCarried(patients);
  if (carried.length === 0) return null;

  return (
    <div
      role="status"
      aria-label={`${carried.length} task${carried.length > 1 ? "s" : ""} carried over from the previous handover`}
      data-testid="carried-over-tasks-banner"
      className="sticky top-0 z-0 -mx-4 border-b border-indigo-700 bg-indigo-600 px-4 py-3 text-sm text-white sm:-mx-6 sm:px-6"
    >
      <div className="flex items-center gap-2 font-semibold">
        <History className="h-4 w-4 shrink-0" aria-hidden="true" />
        {carried.length} task{carried.length > 1 ? "s" : ""} carried over from the previous handover
      </div>
      <ul className="mt-1.5 space-y-1">
        {carried.map((item, i) => (
          <li key={`${item.patientId}-${i}`}>
            <Link
              to={`/session/${sessionId}/patient/${item.patientId}`}
              className="underline-offset-2 hover:underline"
            >
              {item.patientName}, bed {item.bedNumber} — {item.description}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
